import type { Character } from "@/shared/types/character"
import {
  defaultLocale,
  getCharacterContentLocaleForUiLocale,
  type UiLocale,
} from "@/shared/lib/i18n/config"

type DisplayCase = "preserve" | "title"
type LocalizedText = Partial<Record<UiLocale, string>>

interface CharacterContentLocalizationOptions {
  fallbackCase?: DisplayCase
}

export type LocalizedCharacterDisplay = Omit<
  Character,
  | "gender"
  | "personality"
  | "style"
  | "interests"
  | "ethnicity"
  | "hairColor"
  | "eyeColor"
  | "bodyType"
  | "relationshipType"
  | "occupation"
  | "hobby"
  | "additionalPersonalityDetails"
> & {
  gender: string
  personality: string[]
  style: string
  interests: string[]
  ethnicity?: string | null
  hairColor?: string | null
  eyeColor?: string | null
  bodyType?: string | null
  relationshipType?: string | null
  occupation?: string | null
  hobby?: string | null
  additionalPersonalityDetails?: string | null
}

function contentKey(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function toTitleCase(value: string): string {
  return value
    .split(/(\s+|-)/)
    .map((part) => {
      if (/^\s+$|^-$/.test(part) || part.length === 0) return part
      return part.charAt(0).toUpperCase() + part.slice(1)
    })
    .join("")
}

function fallbackValue(value: string, fallbackCase: DisplayCase): string {
  return fallbackCase === "title" ? toTitleCase(value) : value
}

function pickTranslation(translations: LocalizedText | undefined, locale: UiLocale): string | null {
  if (locale === defaultLocale) return null
  return translations?.[locale]?.trim() || null
}

const termTranslations: Record<string, LocalizedText> = {
  adventurous: { fr: "aventureuse", es: "aventurera", nl: "avontuurlijk", ja: "冒険好き" },
  ambitious: { fr: "ambitieuse", es: "ambiciosa", nl: "ambitieus", ja: "野心的" },
  asian: { fr: "asiatique", es: "asiática", nl: "Aziatisch", ja: "アジア系" },
  athletic: { fr: "athlétique", es: "atlética", nl: "atletisch", ja: "アスリート体型" },
  average: { fr: "moyenne", es: "media", nl: "gemiddeld", ja: "標準体型" },
  black: { fr: "noire", es: "negra", nl: "Zwart", ja: "黒人系" },
  bold: { fr: "audacieuse", es: "atrevida", nl: "gedurfd", ja: "大胆" },
  caring: { fr: "attentionnée", es: "cariñosa", nl: "zorgzaam", ja: "思いやりがある" },
  confident: { fr: "sûre d'elle", es: "segura de sí misma", nl: "zelfverzekerd", ja: "自信家" },
  curvy: { fr: "pulpeuse", es: "con curvas", nl: "rondingen", ja: "曲線的" },
  dominant: { fr: "dominante", es: "dominante", nl: "dominant", ja: "支配的" },
  dreamy: { fr: "rêveuse", es: "soñadora", nl: "dromerig", ja: "夢見がち" },
  female: { fr: "femme", es: "mujer", nl: "vrouw", ja: "女性" },
  fierce: { fr: "féroce", es: "feroz", nl: "fel", ja: "情熱的" },
  flirty: { fr: "séduisante", es: "coqueta", nl: "flirterig", ja: "誘惑的" },
  gentle: { fr: "douce", es: "dulce", nl: "zachtaardig", ja: "優しい" },
  hispanic: { fr: "hispanique", es: "hispana", nl: "Hispanisch", ja: "ヒスパニック系" },
  hourglass: { fr: "sablier", es: "reloj de arena", nl: "zandloperfiguur", ja: "砂時計体型" },
  intellectual: { fr: "intellectuelle", es: "intelectual", nl: "intellectueel", ja: "知的" },
  latina: { fr: "latina", es: "latina", nl: "Latina", ja: "ラテン系" },
  loyal: { fr: "loyale", es: "leal", nl: "loyaal", ja: "一途" },
  male: { fr: "homme", es: "hombre", nl: "man", ja: "男性" },
  mysterious: { fr: "mystérieuse", es: "misteriosa", nl: "mysterieus", ja: "ミステリアス" },
  nurturing: { fr: "maternelle", es: "protectora", nl: "verzorgend", ja: "世話好き" },
  petite: { fr: "menue", es: "petite", nl: "petite", ja: "小柄" },
  playful: { fr: "joueuse", es: "juguetona", nl: "speels", ja: "遊び心がある" },
  realistic: { fr: "réaliste", es: "realista", nl: "realistisch", ja: "リアル" },
  romantic: { fr: "romantique", es: "romántica", nl: "romantisch", ja: "ロマンチック" },
  sarcastic: { fr: "sarcastique", es: "sarcástica", nl: "sarcastisch", ja: "皮肉屋" },
  shy: { fr: "timide", es: "tímida", nl: "verlegen", ja: "内気" },
  slender: { fr: "mince", es: "esbelta", nl: "slank", ja: "細身" },
  slim: { fr: "mince", es: "delgada", nl: "slank", ja: "スリム" },
  submissive: { fr: "soumise", es: "sumisa", nl: "onderdanig", ja: "従順" },
  trans: { fr: "trans", es: "trans", nl: "trans", ja: "トランス" },
  voluptuous: { fr: "voluptueuse", es: "voluptuosa", nl: "voluptueus", ja: "豊満" },
  white: { fr: "blanche", es: "blanca", nl: "Wit", ja: "白人系" },
  witty: { fr: "spirituelle", es: "ingeniosa", nl: "gevat", ja: "機知に富む" },

  "after-class-conversations": {
    fr: "conversations après les cours",
    es: "conversaciones después de clase",
    nl: "gesprekken na de les",
    ja: "放課後の会話",
  },
  anime: { fr: "anime", es: "anime", nl: "anime", ja: "アニメ" },
  baking: { fr: "pâtisserie", es: "hornear", nl: "bakken", ja: "お菓子作り" },
  "being-noticed": { fr: "être remarquée", es: "ser notada", nl: "opgemerkt worden", ja: "気づかれること" },
  "bold-lingerie": { fr: "lingerie audacieuse", es: "lencería atrevida", nl: "gedurfde lingerie", ja: "大胆なランジェリー" },
  bookshops: { fr: "librairies", es: "librerías", nl: "boekwinkels", ja: "書店" },
  boxing: { fr: "boxe", es: "boxeo", nl: "boksen", ja: "ボクシング" },
  "bratty-behavior": { fr: "attitude provocante", es: "comportamiento caprichoso", nl: "brutaal gedrag", ja: "わがままな態度" },
  "classical-music": { fr: "musique classique", es: "música clásica", nl: "klassieke muziek", ja: "クラシック音楽" },
  cleaning: { fr: "ménage", es: "limpieza", nl: "schoonmaken", ja: "掃除" },
  clubbing: { fr: "sorties en club", es: "salir de fiesta", nl: "clubben", ja: "クラブ通い" },
  "competitive-gaming": { fr: "jeu compétitif", es: "gaming competitivo", nl: "competitief gamen", ja: "対戦ゲーム" },
  cooking: { fr: "cuisine", es: "cocina", nl: "koken", ja: "料理" },
  "corporate-law": { fr: "droit des affaires", es: "derecho corporativo", nl: "ondernemingsrecht", ja: "企業法務" },
  cuddles: { fr: "câlins", es: "abrazos", nl: "knuffels", ja: "抱きしめること" },
  "digital-art": { fr: "art numérique", es: "arte digital", nl: "digitale kunst", ja: "デジタルアート" },
  "erotic-literature": { fr: "littérature érotique", es: "literatura erótica", nl: "erotische literatuur", ja: "官能文学" },
  fashion: { fr: "mode", es: "moda", nl: "mode", ja: "ファッション" },
  "fine-dining": { fr: "gastronomie", es: "alta cocina", nl: "fine dining", ja: "高級レストラン" },
  "fine-pens": { fr: "beaux stylos", es: "plumas elegantes", nl: "mooie pennen", ja: "上質なペン" },
  "fish-facts": { fr: "anecdotes sur les poissons", es: "datos sobre peces", nl: "visfeitjes", ja: "魚の豆知識" },
  "fit-men": { fr: "hommes sportifs", es: "hombres en forma", nl: "fitte mannen", ja: "鍛えた男性" },
  gardening: { fr: "jardinage", es: "jardinería", nl: "tuinieren", ja: "ガーデニング" },
  gossip: { fr: "ragots", es: "chismes", nl: "roddels", ja: "ゴシップ" },
  "green-tea": { fr: "thé vert", es: "té verde", nl: "groene thee", ja: "緑茶" },
  gym: { fr: "salle de sport", es: "gimnasio", nl: "sportschool", ja: "ジム" },
  "gyaru-fashion": { fr: "mode gyaru", es: "moda gyaru", nl: "gyaru-mode", ja: "ギャルファッション" },
  headpats: { fr: "caresses sur la tête", es: "palmaditas en la cabeza", nl: "aaitjes over het hoofd", ja: "頭なで" },
  "heavy-eyeshadow": { fr: "maquillage sombre", es: "sombra de ojos intensa", nl: "zware oogschaduw", ja: "濃いアイシャドウ" },
  "hip-hop": { fr: "hip-hop", es: "hip hop", nl: "hiphop", ja: "ヒップホップ" },
  "horror-movies": { fr: "films d'horreur", es: "películas de terror", nl: "horrorfilms", ja: "ホラー映画" },
  "ice-cream": { fr: "glace", es: "helado", nl: "ijs", ja: "アイスクリーム" },
  "investment-strategy": { fr: "stratégie d'investissement", es: "estrategia de inversión", nl: "beleggingsstrategie", ja: "投資戦略" },
  "japanese-literature": { fr: "littérature japonaise", es: "literatura japonesa", nl: "Japanse literatuur", ja: "日本文学" },
  "kickboxing": { fr: "kickboxing", es: "kickboxing", nl: "kickboksen", ja: "キックボクシング" },
  learning: { fr: "apprentissage", es: "aprender", nl: "leren", ja: "学ぶこと" },
  "lingerie-collection": { fr: "collection de lingerie", es: "colección de lencería", nl: "lingeriecollectie", ja: "ランジェリーコレクション" },
  "luxury-cars": { fr: "voitures de luxe", es: "autos de lujo", nl: "luxe auto's", ja: "高級車" },
  manga: { fr: "manga", es: "manga", nl: "manga", ja: "漫画" },
  manicures: { fr: "manucures", es: "manicuras", nl: "manicures", ja: "ネイルケア" },
  massage: { fr: "massage", es: "masajes", nl: "massage", ja: "マッサージ" },
  medicine: { fr: "médecine", es: "medicina", nl: "geneeskunde", ja: "医学" },
  negotiation: { fr: "négociation", es: "negociación", nl: "onderhandelen", ja: "交渉" },
  "nightclubs": { fr: "boîtes de nuit", es: "discotecas", nl: "nachtclubs", ja: "ナイトクラブ" },
  "obscure-manga": { fr: "mangas obscurs", es: "manga poco conocido", nl: "obscure manga", ja: "マイナー漫画" },
  "old-movies": { fr: "vieux films", es: "películas antiguas", nl: "oude films", ja: "古い映画" },
  "oversized-cardigans": { fr: "cardigans oversize", es: "cárdigans grandes", nl: "oversized vesten", ja: "大きめのカーディガン" },
  "oversized-hoodies": { fr: "sweats à capuche oversize", es: "sudaderas grandes", nl: "oversized hoodies", ja: "大きめのパーカー" },
  "physical-affection": { fr: "affection physique", es: "afecto físico", nl: "lichamelijke genegenheid", ja: "スキンシップ" },
  "pop-music": { fr: "pop", es: "música pop", nl: "popmuziek", ja: "ポップミュージック" },
  "psychology-research": { fr: "recherche en psychologie", es: "investigación psicológica", nl: "psychologisch onderzoek", ja: "心理学研究" },
  "rare-books": { fr: "livres rares", es: "libros raros", nl: "zeldzame boeken", ja: "希少本" },
  reading: { fr: "lecture", es: "lectura", nl: "lezen", ja: "読書" },
  "reading-people": { fr: "décrypter les gens", es: "leer a la gente", nl: "mensen lezen", ja: "人を読むこと" },
  "reading-romance-novels": { fr: "romans d'amour", es: "novelas románticas", nl: "romans lezen", ja: "恋愛小説" },
  "reality-tv": { fr: "télé-réalité", es: "reality shows", nl: "reality-tv", ja: "リアリティ番組" },
  "red-wine": { fr: "vin rouge", es: "vino tinto", nl: "rode wijn", ja: "赤ワイン" },
  reggae: { fr: "reggae", es: "reggae", nl: "reggae", ja: "レゲエ" },
  rice: { fr: "riz", es: "arroz", nl: "rijst", ja: "米" },
  salsa: { fr: "salsa", es: "salsa", nl: "salsa", ja: "サルサ" },
  shopping: { fr: "shopping", es: "compras", nl: "shoppen", ja: "買い物" },
  "silk-dresses": { fr: "robes en soie", es: "vestidos de seda", nl: "zijden jurken", ja: "シルクのドレス" },
  singing: { fr: "chant", es: "cantar", nl: "zingen", ja: "歌うこと" },
  "sleeping-in": { fr: "grasses matinées", es: "dormir hasta tarde", nl: "uitslapen", ja: "朝寝坊" },
  "social-media": { fr: "réseaux sociaux", es: "redes sociales", nl: "sociale media", ja: "SNS" },
  "spicy-food": { fr: "plats épicés", es: "comida picante", nl: "pittig eten", ja: "辛い料理" },
  "strategic-teasing": { fr: "taquineries calculées", es: "provocación estratégica", nl: "strategisch plagen", ja: "計算されたからかい" },
  "strong-beer": { fr: "bière forte", es: "cerveza fuerte", nl: "sterk bier", ja: "強いビール" },
  swimming: { fr: "natation", es: "natación", nl: "zwemmen", ja: "水泳" },
  tea: { fr: "thé", es: "té", nl: "thee", ja: "お茶" },
  "tea-ceremonies": { fr: "cérémonies du thé", es: "ceremonias del té", nl: "theeceremonies", ja: "茶道" },
  teasing: { fr: "taquineries", es: "provocar", nl: "plagen", ja: "からかい" },
  telenovelas: { fr: "telenovelas", es: "telenovelas", nl: "telenovela's", ja: "テレノベラ" },
  "thrift-shopping": { fr: "friperies", es: "compras de segunda mano", nl: "kringloopshoppen", ja: "古着屋巡り" },
  "traditional-chinese-cooking": { fr: "cuisine chinoise traditionnelle", es: "cocina china tradicional", nl: "traditioneel Chinees koken", ja: "伝統的な中華料理" },
  trends: { fr: "tendances", es: "tendencias", nl: "trends", ja: "トレンド" },
  "video-games": { fr: "jeux vidéo", es: "videojuegos", nl: "videogames", ja: "ビデオゲーム" },
  wine: { fr: "vin", es: "vino", nl: "wijn", ja: "ワイン" },
  winning: { fr: "gagner", es: "ganar", nl: "winnen", ja: "勝つこと" },
  "working-out": { fr: "entraînement", es: "hacer ejercicio", nl: "trainen", ja: "トレーニング" },
  writing: { fr: "écriture", es: "escritura", nl: "schrijven", ja: "執筆" },
  yoga: { fr: "yoga", es: "yoga", nl: "yoga", ja: "ヨガ" },
}

const colorTranslations: Record<string, LocalizedText> = {
  amber: { fr: "ambre", es: "ámbar", nl: "amber", ja: "琥珀色" },
  black: { fr: "noir", es: "negro", nl: "zwart", ja: "黒" },
  blond: { fr: "blond", es: "rubio", nl: "blond", ja: "金髪" },
  blonde: { fr: "blond", es: "rubio", nl: "blond", ja: "金髪" },
  blue: { fr: "bleu", es: "azul", nl: "blauw", ja: "青" },
  brown: { fr: "marron", es: "marrón", nl: "bruin", ja: "茶色" },
  burgundy: { fr: "bordeaux", es: "borgoña", nl: "bordeauxrood", ja: "バーガンディ" },
  gray: { fr: "gris", es: "gris", nl: "grijs", ja: "灰色" },
  green: { fr: "vert", es: "verde", nl: "groen", ja: "緑" },
  grey: { fr: "gris", es: "gris", nl: "grijs", ja: "灰色" },
  hazel: { fr: "noisette", es: "avellana", nl: "hazelnootkleurig", ja: "ヘーゼル" },
  pink: { fr: "rose", es: "rosa", nl: "roze", ja: "ピンク" },
  purple: { fr: "violet", es: "morado", nl: "paars", ja: "紫" },
  red: { fr: "roux", es: "rojo", nl: "rood", ja: "赤" },
  redhead: { fr: "roux", es: "pelirrojo", nl: "roodharig", ja: "赤毛" },
  white: { fr: "blanc", es: "blanco", nl: "wit", ja: "白" },
}

export function localizeCharacterTerm(
  value: string | null | undefined,
  locale: UiLocale,
  options: CharacterContentLocalizationOptions = {}
): string {
  const trimmed = value?.trim()
  if (!trimmed) return ""
  return (
    pickTranslation(termTranslations[contentKey(trimmed)], locale) ??
    fallbackValue(trimmed, options.fallbackCase ?? "preserve")
  )
}

function localizeCharacterColor(
  value: string | null | undefined,
  locale: UiLocale,
  options: CharacterContentLocalizationOptions = {}
): string {
  const trimmed = value?.trim()
  if (!trimmed) return ""
  return (
    pickTranslation(colorTranslations[contentKey(trimmed)], locale) ??
    fallbackValue(trimmed, options.fallbackCase ?? "preserve")
  )
}

export function localizeCharacterList(
  values: readonly string[] | null | undefined,
  locale: UiLocale,
  options: CharacterContentLocalizationOptions = {}
): string[] {
  return (values ?? [])
    .map((value) => localizeCharacterTerm(value, locale, options))
    .filter(Boolean)
}

export function localizeDelimitedCharacterText(
  value: string | null | undefined,
  locale: UiLocale,
  options: CharacterContentLocalizationOptions = {}
): string {
  const trimmed = value?.trim()
  if (!trimmed) return ""
  const parts = trimmed.split(",").map((part) => part.trim()).filter(Boolean)
  if (parts.length <= 1) return localizeCharacterTerm(trimmed, locale, options)
  return parts.map((part) => localizeCharacterTerm(part, locale, options)).join(", ")
}

function localizedVariant(character: Character, locale: UiLocale) {
  const localization = character.localization
  if (character.isOfficial !== true) return null
  if (!localization || localization.status !== "published") return null
  if (localization.locale !== locale) return null
  if (!character.localizationSourceFingerprint) return null
  if (localization.sourceFingerprint !== character.localizationSourceFingerprint) return null
  return localization
}

function pickVariantText(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed || null
}

export function localizeCharacterDisplay(
  character: Character,
  locale: UiLocale,
  options: CharacterContentLocalizationOptions = {}
): LocalizedCharacterDisplay {
  const contentLocale = getCharacterContentLocaleForUiLocale(locale)
  const fallbackCase = options.fallbackCase ?? "preserve"
  const localization = localizedVariant(character, contentLocale)
  const localizedInterests = localization?.interests?.filter(Boolean)

  return {
    ...character,
    tagline: pickVariantText(localization?.tagline) ?? character.tagline,
    description: pickVariantText(localization?.description) ?? character.description,
    backstory: pickVariantText(localization?.backstory) ?? character.backstory,
    gender: localizeCharacterTerm(character.gender, contentLocale, { fallbackCase: "title" }),
    personality: localizeCharacterList(character.personality, contentLocale, { fallbackCase }),
    style: localizeCharacterTerm(character.style, contentLocale, { fallbackCase: "title" }),
    interests:
      localizedInterests && localizedInterests.length > 0
        ? localizedInterests
        : localizeCharacterList(character.interests, contentLocale, { fallbackCase }),
    ethnicity: localizeCharacterTerm(character.ethnicity, contentLocale, { fallbackCase: "title" }) || character.ethnicity,
    hairColor:
      localizeCharacterColor(character.hairColor, contentLocale, { fallbackCase: "title" }) ||
      character.hairColor,
    eyeColor:
      localizeCharacterColor(character.eyeColor, contentLocale, { fallbackCase: "title" }) ||
      character.eyeColor,
    bodyType: localizeCharacterTerm(character.bodyType, contentLocale, { fallbackCase: "title" }) || character.bodyType,
    relationshipType:
      pickVariantText(localization?.relationshipType) ||
      localizeDelimitedCharacterText(character.relationshipType, contentLocale, { fallbackCase: "title" }) ||
      character.relationshipType,
    occupation:
      pickVariantText(localization?.occupation) ||
      localizeDelimitedCharacterText(character.occupation, contentLocale, { fallbackCase: "title" }) ||
      character.occupation,
    hobby:
      pickVariantText(localization?.hobby) ||
      localizeDelimitedCharacterText(character.hobby, contentLocale, { fallbackCase }) ||
      character.hobby,
    additionalPersonalityDetails:
      localizeDelimitedCharacterText(character.additionalPersonalityDetails, contentLocale, { fallbackCase }) ||
      character.additionalPersonalityDetails,
  }
}
